import * as React from 'react';
import { Input } from '@/components/ui/input';
import { useRouter } from 'next/navigation';
import { format } from 'date-fns';

interface Event {
	id: string;
	name: string;
	description: string;
	start_date: string;
	time: string;
	venue: string;
	location: string;
	image_url: string;
	lowestPrice: number;
}

export function EventSearch() {
	const [events, setEvents] = React.useState<Event[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [showDropdown, setShowDropdown] = React.useState(false);
	const router = useRouter();
	const inputRef = React.useRef<HTMLInputElement>(null);
	const dropdownRef = React.useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	React.useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node) &&
				!inputRef.current?.contains(event.target as Node)
			) {
				setShowDropdown(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const searchEvents = React.useCallback(async (searchTerm: string) => {
		if (!searchTerm) {
			setEvents([]);
			setShowDropdown(false);
			return;
		}

		setLoading(true);
		try {
			const response = await fetch(
				`/api/events/search?q=${encodeURIComponent(searchTerm)}`
			);
			const data = await response.json();
			setEvents(data.events);
			setShowDropdown(true);
		} catch (error) {
			console.error('Error searching events:', error);
		} finally {
			setLoading(false);
		}
	}, []);

	const debouncedSearch = React.useCallback(
		debounce((searchTerm: string) => searchEvents(searchTerm), 300),
		[searchEvents]
	);

	return (
		<div className='relative w-full'>
			<Input
				ref={inputRef}
				className='w-full md:w-1/3 bg-background text-foreground text-lg border-foreground/20 rounded-full border'
				placeholder='Search for events...'
				onChange={(e) => debouncedSearch(e.target.value)}
				onFocus={() => {
					if (events.length > 0) setShowDropdown(true);
				}}
			/>

			{showDropdown && (events.length > 0 || loading) && (
				<div
					ref={dropdownRef}
					className='absolute top-full mt-2 w-full md:w-1/3 bg-background border border-border rounded-lg shadow-lg overflow-hidden z-50'
				>
					{loading ? (
						<div className='p-4 text-center text-muted-foreground'>
							Searching...
						</div>
					) : (
						<div className='max-h-[300px] overflow-y-auto'>
							{events.map((event) => (
								<div
									key={event.id}
									className='p-3 hover:bg-accent cursor-pointer border-b last:border-0'
									onClick={() => {
										router.push(`/events/${event.id}`);
										setShowDropdown(false);
									}}
								>
									<div className='flex justify-between items-center w-full'>
										<span className='font-medium'>{event.name}</span>
										<span className='text-sm text-muted-foreground'>
											{event.lowestPrice === 0
												? 'Free'
												: `From GH₵${event.lowestPrice}`}
										</span>
									</div>
									<div className='flex justify-between items-center w-full text-sm text-muted-foreground'>
										<span>
											{format(new Date(event.start_date), 'EEE, MMM d, yyyy')}{' '}
											at{' '}
											{new Date(`2000-01-01T${event.time}`).toLocaleTimeString(
												'en-US',
												{
													hour: 'numeric',
													minute: 'numeric',
												}
											)}
										</span>
										<span>{event.venue}</span>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
}

// Debounce helper function
function debounce<T extends (...args: any[]) => any>(
	func: T,
	wait: number
): (...args: Parameters<T>) => void {
	let timeout: NodeJS.Timeout;

	return function executedFunction(...args: Parameters<T>) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
}
